/* eslint-disable @typescript-eslint/no-explicit-any */
import { signal, computed, Signal, WritableSignal } from '@angular/core';
import { Observable, firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  State,
} from '@progress/kendo-data-query';
import { FilterUtils } from '../../Data-Query/filter-utils';
import * as _ from 'lodash-es';
import { ODataExSettings } from '@ups/xplat/features';

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * Server detail from an error body (ProblemDetails / plain string). Reads only
 * title, detail and message; never the request body, so no PII can leak.
 */
function readServerDetail(body: unknown): string | null {
  if (typeof body === 'string') {
    return readString(body)?.slice(0, 300) ?? null;
  }
  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    const parts = [b['title'], b['detail'], b['message']]
      .map(readString)
      .filter((x): x is string => !!x);
    return parts.length ? parts.join(' - ').slice(0, 300) : null;
  }
  return null;
}

/**
 * Builds a meaningful Error from anything a loader can reject with.
 * HttpErrorResponse is not an Error subclass, so String(err) gave
 * "[object Object]" and lost the status and url (Bug 36943).
 * The original rejection is kept as `cause`.
 */
export function toLoadError(err: unknown): Error {
  if (err instanceof Error) {
    return err;
  }
  let message: string;
  if (err && typeof err === 'object') {
    const e = err as Record<string, unknown>;
    const looksHttp = typeof e['status'] === 'number';
    if (looksHttp) {
      const head = [
        `HTTP ${e['status']}`,
        readString(e['statusText']),
        readString(e['url']),
      ]
        .filter((x) => !!x)
        .join(' ');
      const detail = readServerDetail(e['error']) ?? readString(e['message']);
      message = detail ? `${head}: ${detail}` : head;
    } else {
      message =
        readString(e['message']) ??
        readServerDetail(e['error']) ??
        safeStringify(err);
    }
  } else {
    message = String(err);
  }
  const error = new Error(message, { cause: err });
  error.name = 'GroupingExLoadError';
  return error;
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return Object.prototype.toString.call(value);
  }
}

/**
 * Signal-based GroupingExViewModel for modern Angular with better reactivity
 *
 * Usage:
 * ```typescript
 * this.viewModel = new GroupingExSignalViewModel(
 *   (odataQuery) => this.service.query(odataQuery),
 *   (left, right) => left.id === right.id
 * ).configure(config => {
 *   config.loadDataOnInit = false;
 * });
 * ```
 */
export class GroupingExSignalViewModel {
  // Configuration signals
  public readonly loadDataOnInit = signal(true);
  public readonly loadGroupsByLevels = signal(true);
  public readonly loadAllItemsToGroups = signal(false);
  public readonly autoExpandGroupOnSelection = signal(false);
  public readonly loadAllItemsUntilCountOf = signal(25);

  // State signals
  public readonly loading: WritableSignal<boolean> = signal(false);
  public readonly error: WritableSignal<Error | null> = signal(null);
  public readonly lastLoadedData: WritableSignal<any> = signal(null);

  // Configuration (non-signal, as they're typically set once)
  public linkedAggregateFields: string[][] = [];
  public customODataAggregateExpression: string = undefined;
  public extraFilter: FilterDescriptor | CompositeFilterDescriptor;
  public extraFilterLogic: 'and' | 'or' = 'and';
  public oDataSettings: ODataExSettings;
  public oDataOpenTypesFieldName: string;

  // Computed signals
  public readonly isLoading: Signal<boolean> = computed(() => this.loading());
  public readonly hasError: Signal<boolean> = computed(
    () => this.error() !== null
  );

  constructor(
    private dataLoadCallback: (
      odataQuery: string,
      signal?: AbortSignal
    ) => Observable<any> | Promise<any>,
    private equalityComparerCallback?: (left: any, right: any) => boolean
  ) {
    // Constructor
  }

  /**
   * Load data and update signals
   */
  public async loadData(
    odataQuery: string,
    // eslint-disable-next-line @typescript-eslint/no-shadow -- long-standing param name; the abort signal, not the Angular signal()
    signal?: AbortSignal
  ): Promise<any> {
    this.error.set(null);

    try {
      if (signal?.aborted) {
        throw new Error('Aborted');
      }

      const result = this.dataLoadCallback(odataQuery, signal);

      let data: any;
      if (result instanceof Observable) {
        if (signal) {
          const cancel$ = new Subject<void>();
          const abortHandler = () => {
            cancel$.next();
            cancel$.complete();
          };
          signal.addEventListener('abort', abortHandler, { once: true });
          try {
            data = await firstValueFrom(result.pipe(takeUntil(cancel$)));
          } finally {
            signal.removeEventListener('abort', abortHandler);
          }
        } else {
          data = await firstValueFrom(result);
        }
      } else {
        data = await result;
      }

      if (signal?.aborted) {
        throw new Error('Aborted');
      }

      this.lastLoadedData.set(data);
      return data;
    } catch (err) {
      if (err instanceof Error && err.message === 'Aborted') {
        return null;
      }
      // If firstValueFrom throws EmptyError because of takeUntil, treat as aborted
      if (err.name === 'EmptyError') {
        return null;
      }
      // Not logged here: the caller (GroupingExSignalDirective) logs the
      // rethrown error once, so the log line carries the enriched message.
      const error = toLoadError(err);
      this.error.set(error);
      throw error;
    }
  }

  /**
   * Get data as Observable (for backward compatibility)
   */
  public getDataLoadObservable(odataQuery: string): Observable<any> {
    const result = this.dataLoadCallback(odataQuery);
    return result instanceof Observable
      ? result
      : new Observable((subscriber) => {
          (result as Promise<any>)
            .then((data) => {
              subscriber.next(data);
              subscriber.complete();
            })
            .catch((err) => subscriber.error(err));
        });
  }

  public getEqualityComparer(): (left: any, right: any) => boolean {
    return this.equalityComparerCallback || ((left, right) => left === right);
  }

  public configure(
    configureCallback: (config: GroupingExSignalViewModel) => void
  ): GroupingExSignalViewModel {
    configureCallback(this);
    return this;
  }

  public createStateWithExtraFilter(state: State): State {
    const stateCopy = _.cloneDeep(state) as State;
    FilterUtils.replaceFilter(
      stateCopy,
      FilterUtils.concatFilters(
        stateCopy.filter,
        this.extraFilter,
        this.extraFilterLogic
      )
    );
    return stateCopy;
  }

  /**
   * Clear error state
   */
  public clearError(): void {
    this.error.set(null);
  }

  /**
   * Reset all state
   */
  public reset(): void {
    this.loading.set(false);
    this.error.set(null);
    this.lastLoadedData.set(null);
  }
}
